import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { Button, Card, Flex, Form, Popconfirm, Radio, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useApproveAllPendingCommissionPromotions } from "../../../../common/api/queries";
import { rowGutter } from "../../../../common/constants";
import { AgentCommonFilterFields } from "../../../agent/components/commonfilters/AgentCommonFilterFields";
import { AgentExtendedFilterModalForm } from "../../../agent/components/commonfilters/AgentExtendedFilterModalForm";
import { DEFAULT_COMMISSION_PROMOTIONS_QUERY_PARAMS } from "../api";
import { CommissionPromotionStatus, CommissionPromotionType } from "../enums";
import { CommissionPromotionFilterPageRequest } from "../types";

type ExtendedFilter = { completedEducationDateRange?: string[]; completedAcademyDateRange?: string[] };

type Props = {
  searchParams: CommissionPromotionFilterPageRequest;
};

export const CommissionPromotionsFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const approveAllPendingCommissionPromotions = useApproveAllPendingCommissionPromotions();

  const [form] = Form.useForm<CommissionPromotionFilterPageRequest & ExtendedFilter>();
  const promotionTypes = Form.useWatch("promotionTypes", form);

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      completedEducationDateRange:
        searchParams.completedEducationDateMin && searchParams.completedEducationDateMax
          ? [searchParams.completedEducationDateMin, searchParams.completedEducationDateMax]
          : undefined,
      completedAcademyDateRange:
        searchParams.completedAcademyDateMin && searchParams.completedAcademyDateMax
          ? [searchParams.completedAcademyDateMin, searchParams.completedAcademyDateMax]
          : undefined,
    });
  }, [searchParams]);

  const handleFilterSubmit = ({
    completedEducationDateRange,
    completedAcademyDateRange,
    ...filter
  }: CommissionPromotionFilterPageRequest & ExtendedFilter) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_COMMISSION_PROMOTIONS_QUERY_PARAMS),
            pageIndex: undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("commissions.promotions.titles.list")}</h2>}
      extra={
        <Popconfirm
          title={t("commissions.promotions.helpers.approveAllConfirm")}
          icon={<AntIcon type="question" />}
          cancelText={t("common.no")}
          okText={t("common.yes")}
          onConfirm={() =>
            approveAllPendingCommissionPromotions.mutate(promotionTypes?.[0] ?? CommissionPromotionType.LEVEL)
          }
        >
          <SecondaryButton>{t("commissions.promotions.actions.approveAll")}</SecondaryButton>
        </Popconfirm>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFilterSubmit} name="commissionPromotionsFilterForm">
        <Flex justify="center" className="margin-bottom-large">
          <Form.Item
            name="promotionTypes"
            getValueProps={(value) => ({ value: value && Array.isArray(value) ? value[0] : undefined })}
            initialValue={[CommissionPromotionType.LEVEL]}
            noStyle
          >
            <Radio.Group
              size="large"
              buttonStyle="solid"
              optionType="button"
              onChange={(e) => {
                form.setFieldsValue({ promotionTypes: [e.target.value] });
                handleFilterSubmit(form.getFieldsValue());
              }}
              options={Object.values(CommissionPromotionType).map((type) => ({
                label: t(`commissions.promotions.enums.commissionPromotionType.${type}`),
                value: type,
              }))}
            />
          </Form.Item>
        </Flex>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <AgentCommonFilterFields>
            <Form.Item
              name="promotionStatuses"
              label={t("commissions.promotions.enums.commissionPromotionStatus._label")}
            >
              <Select
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                options={Object.values(CommissionPromotionStatus).map((status) => ({
                  label: t("commissions.promotions.enums.commissionPromotionStatus." + status),
                  value: status,
                }))}
              />
            </Form.Item>
          </AgentCommonFilterFields>
        </div>

        <Flex justify="space-between">
          <AgentExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              handleFilterSubmit(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
